import _ from 'lodash';
import React from 'react'; import { Link } from 'react-router-dom';
import { WppTypography, WppTag, WppActionButton, WppIconExternalLink, WppTooltip } from 'buildingBlocks';
import { DSP } from 'constantsBase';
import StratTypeBadge from 'components/StratTypeBadge';
import { strategyTableColumns } from 'containers/StrategiesList/style';
import { isDspCrossPlatform, getDspLinkForFlight } from 'containers/StrategiesList/utils';
import { EnhancedStrategy, Flight } from 'utils/types';
import { truncateMiddle } from 'utils/formattingUtils';
import { STRATEGY_NAME_ALLOWED_LENGTH } from 'containers/StrategiesList/constants';
import { dspQuickLinkStyles } from './style';
import 'containers/StrategiesList/style.scss';

const { dspClickable, dspNameContainer, strategyNameLink } = dspQuickLinkStyles;
const {
  brandAdvMemberContainer,
  strategyTypeDSPContainer,
  strategyDetail,
  strategiesContainer,
  testLabel,
  strategyDetails,
  strategyTypeBadge,
  separatorCircleShape,
} = strategyTableColumns;

const CircleSeparatorShape = () => (<span style={separatorCircleShape} />);

type StrategyInfoProps = {
  strategy: EnhancedStrategy
  onDspClick: (dspId: number, flights: Array<Flight>) => void
};

const StrategyInfo: React.FC<StrategyInfoProps> = ({
  strategy,
  onDspClick,
}) => {
  const { id, dspId, name, clientTest, strategyType, flights, brand, advertiser, member } = strategy;
  const dspContent = DSP.getById(_.toNumber(dspId));
  const dspDisplayName = _.get(dspContent, 'code', '');
  const isMultiple = isDspCrossPlatform(dspId);

  const handleDspClick = () => {
    if (isMultiple) {
      onDspClick(dspId, _.values(strategy.flights));
      return;
    }
    // this clones the flights and adds the member which is required for retrieving the member's region
    const flightsCopy = _.map(_.cloneDeep(flights), (flight) => {
      _.set(flight, 'advertiser', _.get(strategy, 'advertiser'));
      _.set(flight, 'member', _.get(strategy, 'member'));
      return flight;
    });
    const link = getDspLinkForFlight(_.first(flightsCopy));
    if (link) {
      window.open(link, '_blank');
    }
  };

  const renderDspContent = () => (
    <WppActionButton
      variant="secondary"
      style={dspClickable}
      onClick={handleDspClick}
      className="dspClickableActionButton"
    >
      <WppIconExternalLink slot="icon-start" />
      {isMultiple ? _.capitalize(dspDisplayName) : dspDisplayName}
    </WppActionButton>
  );

  const renderStrategyDetails = () => {
    const details = [['brand', _.get(brand, 'name', '')], ['advertiser', _.get(advertiser, 'name')], ['member', _.get(member, 'displayName')]];
    const validDetails = _.filter(details, ([, value]) => !_.isEmpty(value));
    return _.map(validDetails, ([key, value], index) => (
      <React.Fragment key={key}>
        <WppTypography tag="span" key={key} style={strategyDetails} type="xs-body">{value}</WppTypography>
        {index < _.size(validDetails) - 1 && <CircleSeparatorShape />}
      </React.Fragment>
    ));
  };

  return (
    <div style={strategiesContainer}>
      <WppTooltip text={name} config={{ placement: 'right' }} externalClass="strategyNameTooltip">
        <WppTypography tag="span" type="s-midi">
          <Link className="strat-info-name" to={`/strategies/${id}`} style={strategyNameLink}>
            {_.size(name) > STRATEGY_NAME_ALLOWED_LENGTH
              ? truncateMiddle(name, 60, 60, 150)
              : name}
          </Link>
        </WppTypography>
      </WppTooltip>
      <div style={strategyDetail}>
        <div style={brandAdvMemberContainer}>
          {renderStrategyDetails()}
        </div>
      </div>
      <div style={strategyTypeDSPContainer}>
        <StratTypeBadge stratType={_.get(strategyType, 'displayName')} style={strategyTypeBadge} />
        <div style={dspNameContainer}>
          {renderDspContent()}
        </div>
        {clientTest && (
          <WppTag
            label="Test"
            variant="warning"
            className="tag"
            style={testLabel}
          />
        )}
      </div>
    </div>
  );
};

export default StrategyInfo;
